import React from "react";
import { Link } from "react-router-dom";
import { FileText, Newspaper, BarChart3, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/site/primitives";

// "Your impact" now points supporters to the pages that demonstrate results.
const IMPACT_LINKS = [
  {
    id: "annual-reports",
    icon: FileText,
    title: "Annual Reports",
    body: "Season-by-season outcomes, financials, and the farmers and partners who made it possible.",
    to: "/impact#annual-reports",
    cta: "Read the reports",
    image: "/images/wf/vlai/vlai-3527.jpg",
    alt: "A wide view of farmers working rows of crops with rolling hills beyond.",
  },
  {
    id: "in-the-news",
    icon: Newspaper,
    title: "In the News",
    body: "Coverage from WBUR, The Boston Globe, the BBC, CBS Boston, and more on what your support makes possible.",
    to: "/media#in-the-news",
    cta: "See press coverage",
    image: "/images/wf/vlai/vlai-3810.jpg",
    alt: "A black-and-white portrait of a farmer in a wide-brimmed hat tending crops.",
  },
  {
    id: "by-the-numbers",
    icon: BarChart3,
    title: "Impact by the Numbers",
    body: "275 farmers in our program, 135 acres in production, 140+ communities served, and farmers moving toward independent farm businesses.",
    to: "/impact",
    cta: "Explore our impact",
    image: "/images/wf/farm-36.jpg",
    alt: "A smiling farmer in a plaid shirt and sun hat standing in a bright field of greens.",
  },
];

export const ImpactSection = () => (
  <section id="impact" aria-labelledby="impact-heading" className="wf-section bg-white">
    <div className="wf-container">
      <Reveal className="max-w-2xl">
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-green-text)]">Your impact</span>
        <h2 id="impact-heading" className="mt-3 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">See what your gift makes possible</h2>
        <p className="mt-3 max-w-[56ch] text-base leading-7 text-muted-foreground">Explore our website to learn more about your potential impact, start with one of these pages below.</p>
      </Reveal>
      <ul className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="support-impact-links">
        {IMPACT_LINKS.map((l, i) => (
          <Reveal key={l.id} delay={0.06 * i} as="li" className="h-full">
            <Link
              to={l.to}
              data-testid={`support-impact-${l.id}`}
              className="group flex h-full flex-col overflow-hidden border border-[var(--wf-border)] bg-[var(--wf-paper)] shadow-[var(--wf-shadow-sm)] transition-[box-shadow,transform] duration-300 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
            >
              <div className="relative aspect-[16/9] overflow-hidden bg-[var(--wf-cream)]">
                <img src={l.image} alt={l.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[600ms] ease-out motion-safe:group-hover:scale-105" />
                <span className="absolute left-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(255,253,248,0.92)] text-[var(--wf-maroon)] backdrop-blur-sm"><l.icon className="h-5 w-5" aria-hidden="true" /></span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-serif text-xl font-semibold text-[var(--wf-ink)] group-hover:text-[var(--wf-maroon)]">{l.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-6 text-[var(--wf-soil)]">{l.body}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--wf-maroon)]">{l.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" /></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);
